<template>
  <div class="notifications-dashboard info details">
    <h3>Project Notification Settings</h3>

    <section>
      <h4 class="block-header">
        Immediate updates
      </h4>
      <p class="helper-text">
        Get notified immediately on project changes
      </p>
      <div @click="setDirty" class="material-radio">
        <input
          type="radio"
          id="immediateAll"
          value="all"
          v-model="immediate"
        />
        <label for="immediateAll">All</label>
      </div>
      <div @click="setDirty" class="material-radio">
        <input
          type="radio"
          id="immediateNone"
          value="none"
          v-model="immediate"
        />
        <label for="immediateNone">None</label>
      </div>
    </section>

    <section>
      <h4 class="block-header">
        Daily activity summary
      </h4>
      <p class="helper-text">
        Receive a summary once a day
      </p>
      <div @click="setDirty" class="material-radio">
        <input type="radio" id="dailyAll" value="all" v-model="daily" />
        <label for="dailyAll">All</label>
      </div>
      <div @click="setDirty" class="material-radio">
        <input type="radio" id="dailyNone" value="none" v-model="daily" />
        <label for="dailyNone">None</label>
      </div>
    </section>

    <section>
      <h4 class="block-header">
        Regular oversight
      </h4>
      <p class="helper-text">
        Receive eMail listing all the active projects and their status
      </p>
      <div class="daily-inputs">
        <div @click="setDirty" class="material-radio">
          <input
            type="radio"
            id="oversightDaily"
            value="daily"
            v-model="oversight"
          />
          <label for="oversightDaily">Weekly on</label>
        </div>
        <div class="daily-options-wrap">
          <div class="daily-options">
            <app-checkbox
              v-for="option in daylyOptions"
              :key="option.value"
              :value="oversightDays.includes(option.value)"
              :label="option.label"
              :name="option.value.toString()"
              :isDisabled="oversight === 'none'"
              class="app-checkbox"
              @change="updateOversightDays"
            ></app-checkbox>
          </div>
          <div v-if="errorMessage && noSelectedDays" class="error-inline">
            {{ errorMessage }}
          </div>
        </div>
      </div>
      <div @click="setDirty" class="material-radio">
        <input
          type="radio"
          id="oversightNone"
          value="none"
          v-model="oversight"
        />
        <label for="oversightNone">None</label>
      </div>
    </section>

    <transition name="slide" mode="out-in">
      <div v-if="isWarning && isDirty" class="warning-message">
        <p>
          With these settings you will not receive any updates about Asoko
          Insight projects. Are you sure you want to continue?
        </p>
      </div>
    </transition>

    <div class="align-center">
      <button
        @click="saveSettings"
        class="cta-button high-button-dark"
        :disabled="isLoading"
      >
        <span v-if="!isLoading">Save</span>
        <button-spinner v-if="isLoading" style="width: 20px; height: 20px;" />
      </button>
    </div>
  </div>
</template>

<script>
import appCheckbox from 'ui-components/components/app-checkbox.vue'
import buttonSpinner from 'ui-components/components/spinner'

export default {
  name: 'notifications-dashboard',
  components: {
    appCheckbox,
    buttonSpinner,
  },

  data() {
    return {
      isLoading: false,
      isDirty: false,
      errorMessage: '',
      daily: '',
      immediate: '',
      oversight: '',
      oversightDays: [],
      daylyOptions: [
        { label: 'Sun', value: 0 },
        { label: 'Mon', value: 1 },
        { label: 'Tue', value: 2 },
        { label: 'Wed', value: 3 },
        { label: 'Thu', value: 4 },
        { label: 'Fri', value: 5 },
        { label: 'Sat', value: 6 },
      ],
    }
  },

  computed: {
    appliedSettings() {
      return this.$store.getters['settings/notifications']
    },
    isWarning() {
      return (
        this.oversight === 'none' &&
        this.daily === 'none' &&
        this.immediate === 'none'
      )
    },
    noSelectedDays() {
      return this.oversight === 'daily' && !this.oversightDays.length
    },
    isProjectsPage() {
      return this.$route.path.includes('/projects/settings')
    },
  },

  created() {
    this.showAppliedValues(this.appliedSettings)
    this.$store.dispatch('settings/getProjectsSettings')
  },

  watch: {
    appliedSettings(data) {
      if (data) {
        this.showAppliedValues(data)
      }
    },
  },

  methods: {
    showAppliedValues(values) {
      if (values.daily) {
        this.daily = values.daily
      }
      if (values.immediate) {
        this.immediate = values.immediate
      }
      if (values.oversight) {
        this.oversightDays = [].concat(values.oversight)
        if (values.oversight.length) {
          this.oversight = 'daily'
        } else {
          this.oversight = 'none'
        }
      }
    },
    saveSettings() {
      this.errorMessage = ''
      const { daily, immediate, oversightDays } = this
      if (this.oversight === 'daily' && !oversightDays.length) {
        this.errorMessage = 'At least one day should be selected'
        return
      }
      let oversight = []
      if (this.oversight === 'daily') {
        oversight = [].concat(oversightDays)
      }
      this.isLoading = true
      this.$store
        .dispatch(`settings/setNotificationsSettings`, {
          daily,
          immediate,
          oversight,
        })
        .then(res => {
          this.isLoading = false
          this.$store.dispatch(
            'showMessage',
            'Your settings were successfully updated!'
          )
        })
        .catch(err => {
          this.isLoading = false
          this.$store.dispatch('showMessage')
          this.$log(err)
        })
    },
    updateOversightDays(v) {
      const dayNum = parseInt(v.name)
      if (v.value) {
        this.oversightDays.push(dayNum)
      } else {
        this.oversightDays = this.oversightDays.filter(day => {
          return day !== dayNum
        })
      }
    },
    setDirty() {
      this.isDirty = true
    },
  },
}
</script>

<style scoped lang="scss">
@import '~theme/styles/_radio';
.notifications-dashboard {
  padding: 5px 30px 40px 30px;
  margin-top: 50px;
  max-width: 700px;
  section {
    margin-bottom: $margin-m;
  }
}
.notifications-dashboard h3 {
  margin: 0px;
  padding: 0px;
  font-weight: bold;
  font-size: $header-size-3;
  margin-top: 30px;
  margin-bottom: $margin-m;
}
.block-header {
  font-size: $header-size-5;
  margin-bottom: 3px;
}
.error-inline {
  position: absolute;
  font-weight: 500;
  font-size: 13px;
  color: red;
}
.align-center {
  text-align: center;
  margin-top: 25px;
}
.material-radio {
  padding-left: $padding-s;
}
.inputs-block {
  display: flex;
  justify-content: space-between;
  padding: 0px $padding-m;
}
.daily-inputs {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}
.daily-options-wrap {
  position: relative;
}
.daily-options {
  display: flex;
  .app-checkbox {
    display: inline-flex;
    margin-right: $margin-s;
  }
  & ::v-deep label {
    margin-bottom: 0px;
    font-size: 14px;
  }
}
.helper-text {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 0px;
}
.warning-message {
  color: #31708f;
  background: #d9edf7;
  padding: 10px;
  margin-top: 10px;
  max-width: 450px;
  p {
    margin-bottom: 0px;
  }
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-enter,
.slide-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
